// React
import { useEffect, useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"

// MUI Components
import { ThemeProvider } from "@mui/material/styles"
import Box from "@mui/material/Box"
import MenuItem from "@mui/material/MenuItem"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import Select, { SelectChangeEvent } from "@mui/material/Select"
import Dialog from "@mui/material/Dialog"
import CircularProgress from "@mui/material/CircularProgress"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"

import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers"

// Utils
import newAdapter from "../../utils/newAdapter"
import dayjs, { Dayjs } from "dayjs"
import { useTranslation } from "react-i18next"
import jwtDecode from "jwt-decode"
import { useForm } from "react-hook-form"
import Swal from "sweetalert2"

// Styles
import { green } from "@mui/material/colors"
import theme from "../../styles/theme"

// Services
import { getAllActiveBank } from "../../services/MasterDataService"
// Services

// Types
import { Job, TokenPayload, ConfirmJob, Site } from "../../@types"
import match from "autosuggest-highlight/match"
import parse from "autosuggest-highlight/parse"
import { Autocomplete } from "@mui/material"

export interface InputDataToECProps {
  app_form_Data: any
  keepMounted: boolean
  open: boolean
  onClose: (value?: boolean) => void
  onBankData: (value: any) => void
  onActiveStep: (value: number) => void
}

interface RowData {
  app_form_status_id: string | number
  id_card_number: string
  name: string
  bank_account_number: string
  bank: string
}

export default function InputBank(props: InputDataToECProps) {
  const { app_form_Data, onClose, onBankData, onActiveStep, open, ...other } =
    props
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [dataList, setDatalist] = useState<RowData[]>([])
  const [BankList, setBankList] = useState<
    { bank_code: string; bank_name: string }[]
  >([])

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm()

  useEffect(() => {
    if (app_form_Data.length > 0) {
      setDatalist(app_form_Data)
    }

    const fetchAllActiveBank = async () => {
      try {
        const response = await getAllActiveBank("Active")

        if (response.data?.success) {
          setBankList(response?.data?.banks)
        }
      } catch (error) {
        console.log(error)
        navigate("/servererror", { replace: true })
      }
    }

    if (open) {
      fetchAllActiveBank()
    }
  }, [navigate, open])

  // Function to update bank_account_number or bank for a specific row by id
  const updateBankDetails = (
    app_form_status_id: string | number,
    field: "bank_account_number" | "bank",
    value: string
  ) => {
    setDatalist((prevRows) =>
      prevRows.map((row) =>
        row.app_form_status_id === app_form_status_id
          ? { ...row, [field]: value }
          : row
      )
    )
  }

  const isFormValid = useMemo(() => {
    return dataList.every((item) => {
      // If bank is selected, ensure bank account number is valid
      if (item.bank && item.bank.length > 0) {
        return (
          item.bank_account_number && item.bank_account_number.length === 10
        )
      }
      // If no bank is selected, this item is considered valid (not required)
      return true
    })
  }, [dataList])

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        sx={{
          "& .MuiDialog-paper": { width: "100%" },
          "div.MuiDialogContent-root.MuiDialogContent-dividers.css-1yb8g7n-MuiDialogContent-root":
            {
              padding:
                dataList.length < 4 ? "16px 24px 150px 24px" : "16px 24px",
            },
        }}
        maxWidth="lg"
        open={open}
        {...other}
      >
        <DialogTitle
          id="scroll-dialog-title"
          sx={{ fontWeight: "bold", fontSize: { xs: 16, sm: 18 } }}
        >
          {t("Input other data for bank")}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column" }}
        >
          {dataList.map((item: any, index: any) => (
            <Grid
              container
              spacing={3}
              sx={{
                mt: 1.5,
              }}
              key={`bank-item-${item.app_form_status_id}-${index}`}
            >
              <Grid item xs={12} sm={4}>
                <TextField
                  sx={{
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                    "& .css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled":
                      {
                        WebkitTextFillColor: "rgba(0, 0, 0, 0.7)",
                      },
                  }}
                  fullWidth
                  id="full_name"
                  label={t("Full name TH")}
                  placeholder={t("Full name TH")!}
                  value={item.name}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="Bank" error={errors.bank ? true : false}>
                    {t("ธนาคาร") + " *"}
                  </InputLabel>
                  <Select
                    error={errors.bank ? true : false}
                    labelId="Bank"
                    id="Bank"
                    label={t("Bank") + " *"}
                    value={item.bank}
                    onChange={(event: SelectChangeEvent) => {
                      updateBankDetails(
                        item.app_form_status_id,
                        "bank",
                        event.target.value
                      )

                      event.target.value === "" &&
                        updateBankDetails(
                          item.app_form_status_id,
                          "bank_account_number",
                          event.target.value
                        )
                    }}
                    MenuProps={{ sx: { maxHeight: 500 } }}
                    // required
                  >
                    <MenuItem value="">{t("Not specified")}</MenuItem>
                    {BankList.length > 0 &&
                      BankList.map(
                        (item: { bank_code: string; bank_name: string }) => {
                          return (
                            <MenuItem
                              value={item.bank_code}
                              key={`bank-option-${item.bank_code}`}
                            >
                              {item.bank_name}
                            </MenuItem>
                          )
                        }
                      )}
                    {item.bank.length < 0 && (
                      <Typography
                        variant="inherit"
                        color="error"
                        align="left"
                        pt={1}
                        ml={1}
                        fontSize={13}
                      >
                        {t("text_err_required_bank")}
                      </Typography>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  id="bank_account_number"
                  label={t("Bank account number")}
                  placeholder={t("Bank account number")!}
                  value={item.bank_account_number}
                  InputProps={{
                    type: "number",
                    sx: {
                      // mt: 1,
                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                        {
                          display: "none",
                        },
                      "& input[type=number]": {
                        MozAppearance: "textfield",
                      },
                    },
                  }}
                  disabled={item?.bank?.length <= 0 ? true : false}
                  onChange={(event) => {
                    updateBankDetails(
                      item.app_form_status_id,
                      "bank_account_number",
                      event.target.value
                    )
                  }}
                />
                {item.bank.length > 0 &&
                  item.bank_account_number.length > 0 &&
                  (item.bank_account_number.length > 10 ||
                    item.bank_account_number.length < 10) && (
                    <Typography
                      variant="inherit"
                      color="error"
                      align="left"
                      pt={1}
                      ml={1}
                      fontSize={13}
                    >
                      {t("No. position must be at least 10 characters")}
                    </Typography>
                  )}
              </Grid>
            </Grid>
          ))}
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            px: 3,
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                onClose(true)
                onActiveStep(0)
              }}
              sx={{ px: 3 }}
            >
              {t("Cancel")}
            </Button>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              onClose(true)
              onBankData(dataList)
              onActiveStep(2)
            }}
            sx={{ px: 3 }}
            disabled={!isFormValid}
          >
            {t("Next")}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  )
}
